import React from "react";
import type {MbTextOverlay} from "../../lib/source-text-overlay";
import {clampRunScale,maxRunScale,splitRunsIntoLines} from "../../lib/text-runs";
import {INFO_BASE_STACK,captionFontByFamily,type InfoFontStyle} from "../../lib/caption-fonts";
const INFO_FONT=INFO_BASE_STACK,BASE_H=640;
function itemStyle(x:number,y:number):React.CSSProperties{return {position:'absolute',left:`${x*100}%`,top:`${y*100}%`,transform:'translate(-50%, -50%)',whiteSpace:'nowrap'};}
/** 🪶 텍스트 라벨의 ★표시부만★ — 시간 판정이 없는 순수 노드(타임 축을 모른다).
 *
 *  ★왜 분리했나(Phase 3)★: `CoverLabelNode` 는 비공개인 데다 **씬 상대축**(`tRel` vs `o.start/end`)에
 *  묶여 있어, 컴포지션 절대축을 쓰는 자유 레이어가 그대로 쓸 수 없었다. 룩을 복제하면 두 표면이
 *  조용히 갈라지므로, ★그릴 줄 아는 부분★만 떼어 두 소비자가 같은 픽셀을 쓰게 한다.
 *  시간 판정은 각 소비자가 자기 축으로 하고, 통과한 것만 여기로 넘긴다.
 */
export function TextOverlayLabelNode({ o, S, fitToFrame = false, lineHeightEm, font }: {
  o: Omit<MbTextOverlay, "start" | "end">;
  S: number;
  /** 🔤 제목 폰트 따라가기(A안) — 미지정=기본 스택(고지 캡션 등 기존 소비처 픽셀 불변). */
  font?: InfoFontStyle;
  /**
   * 📐 **줄 높이 명시**(고지 캡션 전용 옵트인 · #3233 적대 리뷰 P3-②) — 안 주면 종전대로
   *   `line-height: normal`(글꼴이 정하는 값)이다. 규격 SSOT 가 박스 높이를 **계산으로 검증**하는
   *   고지 경로만 이 값을 넘겨, 계산과 렌더가 같은 수를 쓰게 한다. 기존 자유 오버레이·커버 라벨은
   *   미전달 = 픽셀 불변.
   */
  lineHeightEm?: number;
  /**
   * 📐 **프레임 안 폭 제한 + 줄바꿈**(적대 R2 P2-1) — ★자유 레이어 전용 옵트인★.
   *
   * ★왜 기본값이 false 인가★: 이 노드의 기존 소비처는 **이미 출하된 씬 오버레이**(CoverLabelNode)이고,
   *   그 축의 캔버스 렌더러(`mediabunnyRender.drawTextOverlays`)는 `\n` 분할만 할 뿐 자동 줄바꿈이
   *   없다. 여기서 기본으로 접으면 ①출하된 강조문구의 레이아웃이 말없이 바뀌고 ②remotion 쪽만 접혀
   *   **두 렌더러의 그림이 갈린다**. 새 축(자유 레이어)에만 켜서 기존 축을 1픽셀도 안 건드린다.
   */
  fitToFrame?: boolean;
}) {
  const text = (o.text ?? "").trim();
  if (!text) return null;
  const fs = Math.max(8, (o.sizePct ?? 0.055) * BASE_H * S);
  // 🔤 이 글자만의 서체가 있으면 그것이 이긴다(회장님 2026-09-14) — 없으면 종전대로 호출부가 준 폰트.
  //   ★파일 로드는 `useTemplateFonts` 의 `preloadFamilies` 게이트가 맡는다★(여기서 로드하지 않는다 —
  //   프레임마다 로드를 걸면 렌더가 폰트 없이 지나간다).
  const ownDef = captionFontByFamily(o.fontFamily);
  const ff = ownDef?.family ? `"${ownDef.family}", ${INFO_BASE_STACK}` : (font?.family ?? INFO_FONT);
  const fw = (w: number) => ownDef?.weight ?? font?.weight ?? w;
  const bg = o.bg === "highlight" ? "#F6C445" : o.bg === "box" ? (o.bgColor ?? "rgba(0,0,0,0.55)") : "transparent";
  /**
   * ✒️ **outline 실렌더**(적대 R1 P2-8) — 타입이 지원한다고 적어 놓고 안 그리면 그건 거짓 약속이다.
   *  `-webkit-text-stroke` 대신 ★8방향 text-shadow★ 로 낸다: 캔버스 렌더러·web-renderer·브라우저
   *  Player 어디서나 같은 그림이 나오는 유일한 표현이다(stroke 는 래스터라이저마다 지원이 갈린다).
   *  shadow 와 함께 켜면 둘을 합성한다(둘 다 유저가 명시적으로 켠 것이라 하나를 무시할 수 없다).
   */
  /** ✒️ 글자 크기에 맞춘 외곽선·그림자 — 토막마다 크기가 갈리면 이 값도 따라가야 굵기가 균일해 보인다. */
  const shadowFor = (size: number) => {
    const w = Math.max(1, size * 0.045);
    const outline = o.outline
      ? [[-1, -1], [1, -1], [-1, 1], [1, 1], [0, -1.4], [0, 1.4], [-1.4, 0], [1.4, 0]]
          .map(([dx, dy]) => `${(dx * w).toFixed(2)}px ${(dy * w).toFixed(2)}px 0 #000`).join(", ")
      : null;
    const drop = o.shadow ? `0 ${size * 0.07}px ${size * 0.28}px rgba(0,0,0,0.5)` : null;
    return [outline, drop].filter(Boolean).join(", ") || undefined;
  };
  const textShadow = shadowFor(fs);
  /**
   * 📐 **긴 문구가 프레임 밖으로 나가지 않게**(자유 레이어 전용) — 폭 무제한 + `nowrap` 상속이 겹치면
   *  한 줄짜리 긴 문구가 프레임을 넘어가 잘린 채 렌더된다(되돌릴 창구도 없다).
   *
   *  ★식은 ★순수 기하★ 다(적대 R2 P2-2)★: 중심 배치(translate −50%)이므로 프레임 안에 들어가는
   *  가장 넓은 중앙 박스는 `min(x, 1−x) × 2` 다. **하한을 두지 않는다** — 종전 34% 하한은 x=0.1 에서
   *  좌측으로 −7% 넘치게 만들어, "안 넘는다"는 주석을 거짓으로 만들었다.
   *  ⇒ 가장자리에 놓을수록 박스가 좁아지는 것은 **결함이 아니라 기하의 사실**이고, 유저는 편집 창의
   *  가로 위치 입력으로 안쪽으로 옮겨 넓힐 수 있다(막다른 길 아님).
   */
  const fitPct = Math.min(96, Math.min(o.x, 1 - o.x) * 2 * 100);
  const fitStyle: React.CSSProperties = fitToFrame ? { whiteSpace: "normal", maxWidth: `${fitPct}%` } : {};
  const innerWrap: React.CSSProperties = fitToFrame ? { wordBreak: "keep-all", overflowWrap: "anywhere" } : {};
  const baseColor = o.color ?? (o.bg === "highlight" ? "#1a1400" : "#fff");
  /**
   * 🎨📏 **글자별 색·크기**(회장님 2026-09-15) — `runs` 가 실렸을 때만 타는 경로.
   *
   * ★줄을 각자의 `div` 로 낸다★ — 줄 높이를 **px 로** 못 박아야 큰 글자가 섞인 줄이 혼자 벌어지지
   *   않는다(무단위로 주면 `span` 이 자기 크기로 다시 곱해 줄 간격이 들쭉날쭉해진다).
   * ★세로 정렬은 인라인 기본값 = **밑선**★ — 캔버스 쪽(`drawRunLines`)도 밑선으로 맞춰 두 렌더러의
   *   그림이 같아진다. 줄 높이 계수(1.28)·최대 배수도 같은 값을 쓴다.
   * 줄바꿈은 이미 데이터에 박혀 있으므로 여기서 접지 않는다(`whiteSpace: "pre"`).
   */
  const runScale = maxRunScale(o.runs);
  const runLines = o.runs?.length ? splitRunsIntoLines(o.runs) : null;
  return (
    <div style={{ ...itemStyle(o.x, o.y), ...fitStyle }}>
      <div style={{ background: bg, color: baseColor, fontFamily: ff, fontWeight: fw(o.weight ?? 800), fontSize: fs, lineHeight: lineHeightEm, padding: o.bg && o.bg !== "none" ? `${fs * 0.28}px ${fs * 0.45}px` : 0, borderRadius: fs * 0.18, textShadow: runLines ? undefined : textShadow, whiteSpace: "pre-line", ...innerWrap, textAlign: (o.align ?? "center") as React.CSSProperties["textAlign"] }}>
        {runLines
          ? runLines.map((line, i) => (
              <div key={`rl-${i}`} style={{ lineHeight: `${fs * 1.28 * runScale}px`, whiteSpace: "pre" }}>
                {line.length
                  ? line.map((r, j) => {
                      const size = fs * (clampRunScale(r.scale) ?? 1);
                      return (
                        <span key={`r-${j}`} style={{ fontSize: size, color: r.color ?? undefined, textShadow: shadowFor(size) }}>
                          {r.text}
                        </span>
                      );
                    })
                  : " "}
              </div>
            ))
          : text}
      </div>
    </div>
  );
}

